import React from "react";
import Box from "@mui/material/Box";
import { Controller } from "react-hook-form";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import { styled, useTheme } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Autocomplete from "@mui/material/Autocomplete";
import useMediaQuery from "@mui/material/useMediaQuery";
import "./styles.css";

const MultiSelect = ({
  name,
  control,
  label,
  proplabel,
  options,
  onChange,
  defaultValue,
  width,
  error,
  required,
  hideLabel,
  disabled,
  margin,
  marginTop,
  isMultiple = true
}) => {
  const isMediumScreen = useMediaQuery("(max-width:900px)");

  const StyledAutocomplete = styled(Autocomplete)(() => ({
    width: width || "100%",
    height: "40px",
    margin: margin || "0 10px",
    borderRadius: "6px",
    marginTop: isMediumScreen ? "13px" : ""
  }));

  const CustomTooltip = styled(({ className, ...props }) => (
    <Tooltip {...props} arrow classes={{ popper: className }} />
  ))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: "#fff",
      color: "rgba(0, 0, 0, 0.87)",
      boxShadow: theme.shadows[3],
      padding: theme.spacing(2),
      borderRadius: 8,
      maxWidth: 220,
      fontSize: 13
    }
  }));

  const StyledInputLabel = styled(InputLabel)(() => ({
    marginTop: marginTop ? marginTop : "0",
    top: "-10px",
    marginTop: isMediumScreen ? "13px" : "",
    color: hideLabel ? "white" : "",
    "&.Mui-error": {
      color: hideLabel ? "white" : ""
    },
    "&.Mui-focused": {
      color: hideLabel ? "white" : ""
    }
  }));

  const isSameValue = (left, right) => String(left) === String(right);

  return (
    <Box>
      <FormControl fullWidth error={Boolean(error)} style={{ margin }}>
        {proplabel ? (
          <p className="select-form-label">{proplabel}</p>
        ) : (
          <StyledInputLabel shrink={true} required={required}>
            {label}
          </StyledInputLabel>
        )}

        <Controller
          name={name}
          control={control}
          defaultValue={defaultValue || (isMultiple ? [] : null)}
          render={({ field }) => {
            const selected = isMultiple
              ? Array.isArray(field.value)
                ? field.value
                : field.value != null && field.value !== ""
                ? [field.value]
                : []
              : field.value
              ? [field.value]
              : [];

            const firstItem =
              options.find((opt) => isSameValue(opt.value, selected[0]))?.name ||
              selected[0];

            return (
              <StyledAutocomplete
                multiple={isMultiple}
                disableCloseOnSelect={isMultiple}
                options={options}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) =>
                  isSameValue(option.value, value?.value)
                }
                value={
                  isMultiple
                    ? options.filter((o) =>
                        selected?.some((selectedValue) =>
                          isSameValue(selectedValue, o.value)
                        )
                      )
                    : options.find((o) => isSameValue(o.value, selected[0])) ||
                      null
                }
                onChange={(event, newValue) => {
                  const selectedValues = isMultiple
                    ? newValue.map((opt) => opt.value)
                    : newValue
                    ? [newValue.value]
                    : [];

                  const finalValue = isMultiple
                    ? selectedValues
                    : selectedValues[0] || null;

                  field.onChange(finalValue);
                  onChange?.(finalValue);
                }}
                disabled={disabled}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={''}
                    required={required}
                    error={!!error}
                    helperText={error?.message}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        padding: '0 6px',
                        minHeight: '40px',
                        borderRadius: '6px',
                        backgroundColor: disabled ? '#f5f5f5' : '#fff',
                    
                      },
                      '& .MuiInputLabel-root': {
                        top: '-6px',
                        fontSize: 14,
                      }
                    }}
                  />
                )}
                
                renderTags={(tagValue, getTagProps) => {
                  const visibleTag = tagValue[0];
                  const hiddenCount = tagValue.length - 1;

                  return (
                    <CustomTooltip
                      title={
                        tagValue.length > 0 ? (
                          <React.Fragment>
                            <Typography
                              variant="subtitle2"
                              sx={{ fontWeight: "bold", mb: 1 }}
                            >
                              Seleccionados:
                            </Typography>
                            {tagValue.map((opt) => (
                              <Typography key={opt.value} variant="body2" noWrap>
                                {opt.name}
                              </Typography>
                            ))}
                          </React.Fragment>
                        ) : (
                          "Sin selección"
                        )
                      }
                    >
                      <Chip
                        label={
                          visibleTag?.name +
                          (hiddenCount > 0 ? ` +${hiddenCount}` : "")
                        }
                        style={{ maxWidth: 150 }}
                      />
                    </CustomTooltip>
                  );
                }}
                fullWidth
                sx={{
                  width: width || "100%",
                  mt: isMediumScreen ? 1.5 : 0
                }}
              />
            );
          }}
        />
      </FormControl>
    </Box>
  );
};

export default MultiSelect;
 
